package com.downdogapp.client.singleton

import com.downdogapp.*
import com.downdogapp.client.api.Request
import com.downdogapp.client.currentClockTime
import com.downdogapp.client.singleton.Key.PendingRequest
import com.downdogapp.client.singleton.Key.PendingRequestIds
import com.downdogapp.client.singleton.Platform.WEB
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString

object Network {

  val commonRequestParams = mutableMapOf<String, String?>()

  // These three only accessed on main thread
  private val queuedRequests = mutableListOf<RequestInfo>()
  private var sendingRequests = false
  private var pendingRequestIds = listOf<String>()
    set(value) {
      field = value
      UserPrefs[PendingRequestIds] = value
    }

  init {
    NetworkHelper.toString()
  }

  // Called from NetworkHelper.init()
  fun initPendingRequests() {
    // This has to happen here before UserPrefs might not be initialized
    pendingRequestIds = UserPrefs[PendingRequestIds]
    for (requestId in pendingRequestIds) {
      val key = PendingRequest(requestId)
      val json =
        UserPrefs[key]?.let {
          try {
            JSON.decodeFromString<PendingRequestObj>(it)
          } catch (e: Exception) {
            null
          }
        }
      if (json != null) {
        queuedRequests.add(RequestInfo(json.url, json.params, null, null, requestId))
      } else {
        val message = "Missing or invalid json for $key: ${UserPrefs[key]}"
        if (App.debug) {
          throw Exception(message)
        }
        Logger.logError("invalid_userprefs_json", mapOf("message" to message))
      }
    }
  }

  fun post(request: Request, callback: ((String?) -> Unit)?) {
    post(request.url, request.parameterMap, null, callback)
  }

  // ios and android, for js this is an alias for post()
  fun postAndSave(request: Request, savedResponseKey: Key<String>,
                  callback: ((String?) -> Unit)?) {
    post(request.url, request.parameterMap, savedResponseKey, callback)
  }

  fun post(request: Request) {
    post(request.url, request.parameterMap, null, null)
  }

  private fun post(url: String,
                   requestSpecificParams: Map<String, String?>,
                   savedResponseKey: Key<String>?,
                   callback: ((String?) -> Unit)?) {
    App.runInMain {
      val allParams = mutableMapOf<String, String>()
      for ((key, value) in (requestSpecificParams + commonRequestParams)) {
        if (value != null) {
          allParams[key] = value
        }
      }
      allParams["timestamp"] = currentClockTime().millis.toString()
      App.cred?.let {
        allParams["cred"] = it
      }
      allParams["languageOption"] = UserPrefs[Key.Language] ?: ""

      var pendingRequestId: String? = null
      if (callback == null) {
        pendingRequestId = currentClockTime().millis.toString()
        UserPrefs[PendingRequest(pendingRequestId)] =
          JSON.encodeToString(PendingRequestObj(url = url, params = allParams))
        pendingRequestIds = pendingRequestIds + pendingRequestId
      }
      queuedRequests.add(RequestInfo(url, allParams, callback, savedResponseKey, pendingRequestId))
      if (!sendingRequests) {
        sendingRequests = true
        sendQueuedRequest(true)
      }
    }
  }

  /**
   * Tries to send the first request in [queuedRequests].
   *
   * If it succeeds, the request will be removed from queuedRequests, as will the saved pending
   * request if applicable. If there are still more requests queued, then this will be called again,
   * otherwise the sending process will halt.
   *
   * If it fails after a retry, it will be cancelled as well as any queued requests with callbacks, and the
   * sending process will halt.
   */
  private fun sendQueuedRequest(retry: Boolean) {
    fun onFailure() {
      var index = 0
      while (index < queuedRequests.size) {
        val callback = queuedRequests[index].callback
        if (callback != null) {
          callback.invoke(null)
          queuedRequests.removeAt(index)
        } else {
          index++
        }
      }
      sendingRequests = false
    }
    if (isOffline()) {
      onFailure()
      return
    }
    val request = queuedRequests.first()
    if (App.debug) {
      println(
        "${request.url}?" +
        request
          .params
          .map { "${it.key}=${it.value.replace(" ", "%20")}" }
          .joinToString("&")
      )
    }
    NetworkHelper.makePostRequest(request.url, request.params) { response ->
      App.runInMain {
        if (response != null) {
          if (request.callback != null) {
            if (request.savedResponseKey != null && response.isNotBlank() && App.platform != WEB) {
              UserPrefs[request.savedResponseKey] = response
            }
            request.callback.invoke(response.ifBlank { null })
          }
          request.pendingRequestId?.let { id ->
            pendingRequestIds = pendingRequestIds.filter { it != id }
            UserPrefs.remove(PendingRequest(id))
          }

          queuedRequests.removeAt(0)
          if (queuedRequests.isEmpty()) {
            sendingRequests = false
          } else {
            sendQueuedRequest(true)
          }
        } else {
          if (retry) {
            sendQueuedRequest(false)
          } else {
            // we remove the request that failed even if it doesn't have a callback or else we might be in an
            // endless loop trying to send an invalid request
            queuedRequests.removeAt(0).callback?.invoke(null)
            onFailure()
          }
        }
      }
    }
  }

  fun isOffline() = NetworkHelper.isOffline()

  fun usingCellularConnection() = NetworkHelper.usingCellularConnection()

  fun setReferrerInfo(campaign: String?, channel: String?) {
    campaign?.let {
      commonRequestParams["campaign"] = it
    }
    channel?.let {
      commonRequestParams["channel"] = it
    }
  }

  fun setToken(fcmToken: String) {
    commonRequestParams["fcmToken"] = fcmToken
  }

  @Serializable
  private data class PendingRequestObj(val url: String, val params: Map<String, String>)

  private class RequestInfo(val url: String,
                            val params: Map<String, String>,
                            val callback: ((String?) -> Unit)?,
                            val savedResponseKey: Key<String>?,
                            val pendingRequestId: String?) {

    init {
      if (callback == null) {
        check(pendingRequestId != null) { "Must have pendingRequestKey when no callback" }
        check(savedResponseKey == null) { "Can't have savedResponseKey without callback" }
      } else {
        check(pendingRequestId == null) { "Can't have callback and pendingRequestKey" }
      }
    }
  }
}

expect object NetworkHelper {

  fun makePostRequest(url: String, params: Map<String, String>, callback: ((String?) -> Unit))

  fun isOffline(): Boolean

  fun usingCellularConnection(): Boolean
}
